<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<!-- Summary -->
				<app-tab-item :tab="tab" value="summary">
					<app-heading :title="unit.name" />
					<app-row>
							<app-cell cols="6" sm="3">
								<div>
									<app-icon icon="fa-bath" size="large" />&nbsp;&nbsp;Bathrooms: {{unit.bathrooms}}
								</div>
							</app-cell>
							<app-cell cols="6" sm="3">
								<div>
									<app-icon icon="fa-bed" size="large" />&nbsp;&nbsp;Bedrooms: {{unit.bedrooms}}
								</div>
							</app-cell>
							<app-cell cols="6" sm="3">
								<div>
									<app-icon icon="fa-user-friends" size="large" />
									&nbsp;&nbsp;Occupancy: {{unit.occupancy}}
								</div>
							</app-cell>
							<app-cell cols="6" sm="3">
								<div>
									<app-icon icon="fa-ruler-combined" size="large" />
									&nbsp;&nbsp;Square feet: {{unit.square_feet}}
								</div>
							</app-cell>
						</app-row>
						<app-row>
							<app-cell class="mt-2">
								<h2>Rooms</h2>
							</app-cell>
						</app-row>
						<app-row>
							<app-cell cols="12" sm="3" v-for="(room, i) in unit.rooms" :key="`room-${i}`">
								<app-section class="ma-4">
									<div class="font-weight-bold">Room name</div>
									<div>{{rooms[room.room] || 'Unknown'}}</div>
									<div class="font-weight-bold">Beds</div>
									<div>
										<ul v-if="room.beds && room.beds.length">
											<li v-for="(bed, j) in room.beds" :key="`room-${i}-bed-${j}`">
												{{bed.quantity}}x {{beds[bed.bed] || 'Unknown'}}
											</li>
										</ul>
										<div v-else>None</div>
									</div>
								</app-section>
							</app-cell>
						</app-row>
				</app-tab-item>

				<!-- Features -->
				<app-tab-item :tab="tab" value="features">
					<app-heading :title="`${unit.name} Features`" />
					<app-row>
						<app-cell>
							<app-data-table
								:items="features.items"
								:total="features.count"
								:headers="features.headers"
								@update="fetchFeatures"
							/>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Availabilities -->
				<app-tab-item :tab="tab" value="availabilities">
					<app-heading :title="`${unit.name} Availabilities`" />
					<app-filter @click="applyFilters('availabilities')">
						<app-row no-padding>
							<app-cell cols="6" sm="4">
								<input-date-picker
									v-model="filters.availabilities.startDate"
									label="Start date"
									clearable
									dense
								></input-date-picker>
							</app-cell>
							<app-cell cols="6" sm="4">
								<input-date-picker
									v-model="filters.availabilities.endDate"
									label="End date"
									clearable
									dense
								></input-date-picker>
							</app-cell>
						</app-row>
					</app-filter>
					<app-row>
						<app-cell>
							<app-data-table
								:items="availabilities.items"
								:total="availabilities.count"
								:headers="availabilities.headers"
								@update="fetchAvailabilities"
							>
								<template v-slot:raw="{ item }">
									<app-json :data="item || {}" />
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Channels -->
				<app-tab-item :tab="tab" value="channels">
					<app-heading :title="`${unit.name} Channels`" />
					<app-row>
						<app-cell>
							<app-data-table
								:items="channels.items"
								:total="channels.count"
								:headers="channels.headers"
								@update="fetchChannels"
							>
								<template v-slot:content="{ item }">
									<app-json :data="getUnitChannelContent(item._id)" />
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>

				<!-- Amenities -->
				<app-tab-item :tab="tab" value="amenities">
					<app-heading :title="`${unit.name} Amenities`" />
					<app-row>
						<app-cell>
							<app-data-table
								:items="amenities.items"
								:total="amenities.count"
								:headers="amenities.headers"
								@update="fetchAmenities"
							>
								<template v-slot:details="{ item }">
									<app-json v-if="item.metadata" :data="item.metadata" />
									<span v-else class="text-muted">—</span>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { omitBy, isNil } from 'lodash'

import Tab from '@/types/Tab'
import DataTable from '@/types/DataTable'
import { mapFields } from '@/utils/maps'

export default {
	async created() {
		try {
			this.unit = await this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.resort)
				.to('units')
				.to(this.id)
				.get()
			await Promise.all([this.fetchRooms(), this.fetchBeds()])
		} catch (err) {
			this.$notifications.pushAlert('error', err.data.message)
		}

		this.tabs = [new Tab('summary', {
			name: 'Summary',
			to: { name: 'studeo-resort-unit', params: { resort: this.resort, unit: this.id } },
			value: `/studeo-resorts/${this.resort}/units/${this.id}`,
			exact: true,
		}), new Tab('amenities', {
			name: 'Amenities',
			to: { name: 'studeo-resort-unit-child', params: { resort: this.resort, unit: this.id, utab: 'amenities' } },
			value: `/studeo-resorts/${this.resort}/units/${this.id}/amenities`,
		}), new Tab('features', {
			name: 'Features',
			to: { name: 'studeo-resort-unit-child', params: { resort: this.resort, unit: this.id, utab: 'features' } },
			value: `/studeo-resorts/${this.resort}/units/${this.id}/features`,
		}), new Tab('availabilities', {
			name: 'Availabilities',
			to: { name: 'studeo-resort-unit-child', params: { resort: this.resort, unit: this.id, utab: 'availabilities' } },
			value: `/studeo-resorts/${this.resort}/units/${this.id}/availabilities`,
		}), new Tab('channels', {
			name: 'Channels',
			to: { name: 'studeo-resort-unit-child', params: { resort: this.resort, unit: this.id, utab: 'channels' } },
			value: `/studeo-resorts/${this.resort}/units/${this.id}/channels`,
		})]
	},
	data() {
		return {
			unit: {
				name: '',
				code: '',
				bathrooms: 0,
				bedrooms: 0,
				occupancy: 0,
				rooms: [],
			},
			tabs: [],
			rooms: {},
			beds: {},
			features: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}]),
			availabilities: new DataTable([{
				text: 'Date',
				value: 'date',
				sortable: true,
				format: 'date',
			}, {
				text: 'Amount',
				value: 'amount',
				sortable: true,
				format: 'currency',
			}, {
				text: 'Occupied',
				value: 'occupied',
				sortable: true,
			}, {
				text: 'Available',
				value: 'available',
				sortable: true,
			}, {
				text: 'Total',
				value: 'total',
				sortable: true,
			}, {
				text: 'Minimum stay',
				value: 'min_stay',
				sortable: true,
			}, {
				text: 'Raw content',
				value: 'raw',
				sortable: false,
			}]),
			channels: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Unit content',
				value: 'content',
				sortable: false,
			}]),
			amenities: new DataTable([{
				text: 'ID',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: false,
			}, {
				text: 'Details',
				value: 'details',
				sortable: false,
			}]),

			filters: {
				availabilities: {
					startDate: null,
					endDate: null,
				},
			},
			appliedFilters: {
				availabilities: {},
			},
		}
	},
	computed: {
		resort() {
			return this.$route.params.resort
		},
		id() {
			return this.$route.params.unit
		},
		unitRooms() {
			return (this.unit.rooms || []).map(room => room.room)
		},
		unitBeds() {
			return (this.unit.rooms || []).reduce((acc, room) => [
				...acc,
				...(room.beds && room.beds.length ? room.beds.map(bed => bed.bed) : []),
			], [])
		},
	},
	methods: {
		async fetchRooms() {
			if (!this.unitRooms.length) { return }
			const request = this.$ravello.request()
				.v(2)
				.to('roomtypes')
				.limit(this.unitRooms.length)
				.param('_id', this.unitRooms)

			try {
				const rooms = await request.get()
				this.rooms = mapFields(rooms, 'name')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async fetchBeds() {
			if (!this.unitBeds.length) { return }
			const request = this.$ravello.request()
				.v(2)
				.to('bedtypes')
				.limit(this.unitBeds.length)
				.param('_id', this.unitBeds)

			try {
				const beds = await request.get()
				this.beds = mapFields(beds, 'name')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async fetchFeatures(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.resort)
				.to('units')
				.to(this.id)
				.to('features')
				.fromPagination(pagination)

			try {
				this.features.items = await request.get()
				if (this.features.count === -1) {
					const count = await request.count()
					this.features.count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async fetchAvailabilities(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.resort)
				.to('units')
				.to(this.id)
				.to('availabilities')
				.fromPagination(pagination)

			if (this.appliedFilters.availabilities.startDate) {
				request.param('date', this.appliedFilters.availabilities.startDate, '>=')
			}
			if (this.appliedFilters.availabilities.endDate) {
				request.param('date', this.appliedFilters.availabilities.endDate, '<=')
			}
			try {
				this.availabilities.items = await request.get()
				if (this.availabilities.count === -1) {
					const count = await request.count()
					this.availabilities.count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async fetchChannels(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.resort)
				.to('units')
				.to(this.id)
				.to('channels')
				.fromPagination(pagination)

			try {
				this.channels.items = await request.get()
				if (this.channels.count === -1) {
					const count = await request.count()
					this.channels.count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
		async fetchAmenities(pagination) {
			const request = this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.resort)
				.to('units')
				.to(this.id)
				.to('amenities')
				.fromPagination(pagination)

			try {
				const amenities = await request.get()
				// Extract name from translations.en and details from the amenity object (from the subdocument)
				this.amenities.items = amenities.map((amenity) => {
					// Amenity data might be nested under 'amenity' field or at top level
					const amenityData = amenity.amenity || amenity
					return {
						...amenityData,
						name: this.getTranslation(amenityData.translations, 'en'),
						details: amenity.details || amenityData.details || null,
					}
				})
				if (this.amenities.count === -1) {
					const count = await request.count()
					this.amenities.count = count.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', (err.data && err.data.message) || 'Failed to fetch amenities')
			}
		},
		getTranslation(translations, lang = 'en') {
			if (!translations) return ''
			if (typeof translations === 'string') return translations
			if (translations[lang]) return translations[lang]
			// Fallback to first available translation
			const keys = Object.keys(translations)
			return keys.length > 0 ? translations[keys[0]] : ''
		},

		applyFilters(type) {
			this.appliedFilters[type] = omitBy(this.filters[type], v => isNil(v) || v === '')
			this[type].count = -1
		},

		getUnitChannelContent(id) {
			return this.unit.channels.find(channel => channel.channel === id)
		},
	},
}
</script>
